import React, { useEffect, useState, useCallback, useRef } from "react"
import { useDispatch } from "react-redux"
import { X } from "lucide-react"
import SiteVisitForm, {
  EMPTY_SITE_VISIT_FORM,
  mapSiteVisitToForm,
} from "../../Components/admin/SiteVisitForm"
import { uploadSiteVisitAttachment } from "../../redux/siteVisitSlice"

const getEmptyForm = (adminName) => ({
  ...EMPTY_SITE_VISIT_FORM,
  leaderName: adminName || "",
})

const SiteVisitModal = ({
  isOpen,
  onClose,
  mode = "initiate",
  adminName,
  siteVisit,
  onInitiate,
  onAdminSubmit,
  onAdminDraftSave,
  onAdminPreSave,
  onObservationsSubmit,
  onFeedbackSubmit,
  onReinitiate,
  loading = false,
}) => {
  const dispatch = useDispatch()
  const [formData, setFormData] = useState(() => getEmptyForm(adminName))
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const initializedRef = useRef(false)

  const status = siteVisit?.status || ""
  const canSaveDraft =
    mode === "admin" &&
    (status === "initiated" || status === "observations_captured")

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false
      return
    }

    if (!initializedRef.current) {
      if (siteVisit) {
        setFormData(mapSiteVisitToForm(siteVisit))
      } else {
        setFormData(getEmptyForm(adminName))
      }
      initializedRef.current = true
    }
  }, [isOpen, siteVisit, adminName])

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleUploadAttachment = async (files) => {
    try {
      setUploadingAttachment(true)
      const uploaded = await dispatch(uploadSiteVisitAttachment(files)).unwrap()
      setFormData((prev) => ({
        ...prev,
        meetingPlanAttachments: [
          ...(prev.meetingPlanAttachments || []),
          ...uploaded,
        ],
      }))
    } catch {
      // toast handled in slice
    } finally {
      setUploadingAttachment(false)
    }
  }

  const buildAdminPayload = () => ({
    leaderName: formData.leaderName,
    leaderLocation: formData.leaderLocation,
    administratorRole: formData.administratorRole,
    leaderGoalFocus: formData.leaderGoalFocus,
    cohortType: formData.cohortType,
    meetingPlanContext: formData.meetingPlanContext,
    preConferenceNotes: formData.preConferenceNotes,
    meetingPlanAttachments: formData.meetingPlanAttachments || [],
    evidenceTellsYou: formData.evidenceTellsYou,
    patternsTrendsOutliers: formData.patternsTrendsOutliers,
    collaborativeReflection: formData.collaborativeReflection,
  })

  const handleSaveDraft = () => {
    if (!canSaveDraft) return
    const payload = buildAdminPayload()
    if (onAdminDraftSave) {
      onAdminDraftSave(payload)
      return
    }
    // Fallback for older callers that only wire pre-save
    if (status === "initiated") {
      onAdminPreSave?.(payload)
    }
  }

  const handleSubmit = () => {
    if (mode === "initiate") {
      if (!formData.visitNumber) {
        alert("Please enter a visit number")
        return
      }
      onInitiate?.({
        visitNumber: Number(formData.visitNumber),
        leaderLocation: formData.leaderLocation,
        administratorRole: formData.administratorRole,
        leaderGoalFocus: formData.leaderGoalFocus,
        cohortType: formData.cohortType,
      })
      return
    }

    if (mode === "admin") {
      if (status === "observations_captured") {
        onAdminSubmit?.(buildAdminPayload())
      }
      return
    }

    if (mode === "capture_observations") {
      if (!formData.observationEvidence?.trim()) {
        alert("Please enter Observation/Site Visit Evidence remarks")
        return
      }
      onObservationsSubmit?.({
        observationEvidence: formData.observationEvidence,
      })
      return
    }

    if (mode === "super_admin_evaluator") {
      onFeedbackSubmit?.({
        observationEvidence: formData.observationEvidence,
        conferenceFeedback: formData.conferenceFeedback,
      })
    }
  }

  const getSubmitLabel = () => {
    if (mode === "initiate") return "Initiate Site Visit"
    if (mode === "admin" && status === "observations_captured")
      return "Submit to Super Admin"
    if (mode === "capture_observations") return "Save Observation Evidence"
    if (mode === "super_admin_evaluator") return "Complete Evaluation"
    return "Close"
  }

  const showPrimarySubmit =
    mode !== "view" &&
    !(mode === "admin" && status === "initiated")

  const getModeMessage = () => {
    if (mode === "initiate") {
      return (
        <p className="text-sm text-gray-600 mb-4">
          Initiate a site visit for{" "}
          <span className="font-semibold">{adminName}</span>. The leader can
          complete Pre-Observation next. Post-Observation unlocks after you
          capture Observation Evidence.
        </p>
      )
    }
    if (mode === "admin" && status === "initiated") {
      return (
        <p className="text-sm text-gray-600 mb-4">
          Fill in any Pre-Observation fields and click Save Draft to keep your
          progress. Post-Observation unlocks after the evaluator adds
          Observation/Site Visit Evidence remarks.
        </p>
      )
    }
    if (mode === "admin" && status === "observations_captured") {
      return (
        <p className="text-sm text-gray-600 mb-4">
          Observation evidence is available. Save Draft anytime, then submit to
          Super Admin when Post-Observation is complete.
        </p>
      )
    }
    if (mode === "capture_observations") {
      return (
        <p className="text-sm text-gray-600 mb-4">
          Enter Observation/Site Visit Evidence remarks. This unlocks
          Post-Observation for the leader.
        </p>
      )
    }
    if (mode === "super_admin_evaluator") {
      return (
        <p className="text-sm text-gray-600 mb-4">
          Complete Conference Feedback. The site visit will be locked after
          submission.
        </p>
      )
    }
    if (mode === "view") {
      return (
        <p className="text-sm text-gray-500 mb-4 italic">
          This site visit is completed and locked. No further edits are allowed.
        </p>
      )
    }
    return null
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[60] p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[95vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-[#241763]">
            Observation/Site Visit
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Close site visit modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {mode === "initiate" && (
            <div className="mb-4 max-w-xs">
              <label className="text-sm font-medium block mb-1">
                Visit Number *
              </label>
              <input
                type="number"
                min="1"
                value={formData.visitNumber}
                onChange={(e) => handleChange("visitNumber", e.target.value)}
                className="w-full border border-gray-300 rounded p-2 text-sm"
                aria-label="Visit number"
              />
            </div>
          )}

          {getModeMessage()}

          <SiteVisitForm
            formData={formData}
            onChange={handleChange}
            mode={mode}
            status={status}
            adminName={adminName}
            onUploadAttachment={handleUploadAttachment}
            uploadingAttachment={uploadingAttachment}
            record={siteVisit}
          />
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            {mode === "view" ? "Close" : "Cancel"}
          </button>

          {canSaveDraft && (
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={loading || uploadingAttachment}
              className="px-4 py-2 text-sm border border-[#241763] text-[#241763] rounded hover:bg-[#241763]/5 disabled:opacity-50"
              aria-label="Save draft"
            >
              {loading ? "Please wait..." : "Save Draft"}
            </button>
          )}

          {mode === "super_admin_evaluator" && onReinitiate && (
            <button
              type="button"
              onClick={() => {
                onReinitiate()
                onClose()
              }}
              disabled={loading}
              className="px-4 py-2 text-sm bg-[#ED7D31] text-white rounded hover:opacity-90 disabled:opacity-50"
              aria-label="Reinitiate site visit"
            >
              {loading ? "Please wait..." : "Reinitiate"}
            </button>
          )}

          {showPrimarySubmit && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || uploadingAttachment}
              className="px-4 py-2 text-sm bg-[#241763] text-white rounded hover:bg-[#1a1047] disabled:opacity-50"
            >
              {loading ? "Please wait..." : getSubmitLabel()}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default SiteVisitModal
